import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Group,
  LoadingOverlay,
  Radio,
  Stack,
  TextInput,
  NumberInput,
  ActionIcon,
  Title,
  Breadcrumbs,
  Anchor,
  Card,
  ScrollArea,
  Box,
  Container,
} from "@mantine/core";
import { Link, useLocation } from "react-router-dom";
import { createFormContext } from "@mantine/form";
import { CirclePlus, Copy, Trash } from "tabler-icons-react";
import { ADMIN_PATH } from "_urls";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";

// ----- Types -----
export type SubPlace = {
  id: string;
  title: string;
  numberOfWinners: number;
  status: "enabled" | "disabled";
  index?: number;
};

export type PlaceModel = {
  placeId: string;
  placeTitle: string;
  subPlaces: SubPlace[];
};

// ----- Zod Schemas -----
const subPlaceSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, "Title is required"),
  numberOfWinners: z.number().min(1, "Must have at least 1 winner"),
  status: z.enum(["enabled", "disabled"]),
  index: z.number().optional(),
});

const placeModelSchema = z.object({
  placeId: z.string(),
  placeTitle: z.string().min(1, "Place title is required"),
  subPlaces: z.array(subPlaceSchema),
});

// ----- Create form context -----
export const [PlaceFormProvider, usePlaceFormContext, usePlaceForm] =
  createFormContext<PlaceModel>();

// ----- The form component -----
export function SubPlaceAdditionForm({
  place,
  editMode,
  onSaveLocally,
  setOpened,
}: {
  place?: PlaceModel;
  editMode: boolean;
  onSaveLocally?: (data: PlaceModel) => void;
  setOpened: (opened: boolean) => void;
}) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isDuplicate = location.pathname.endsWith("/duplicate");
  //  const navigate = useNavigate();

  // Provide initial form values based on place and edit mode
  const getInitialValues = useCallback(() => {
    if (place && editMode) {
      return {
        placeId: isDuplicate ? "" : place.placeId,
        placeTitle: place.placeTitle || "",
        subPlaces:
          place.subPlaces?.map((sp, i) => ({
            ...sp,
            id: sp.id || uuidv4(),
            index: i + 1,
          })) || [],
      };
    }
    return {
      placeId: "",
      placeTitle: "",
      subPlaces: [],
    };
  }, [place, editMode, isDuplicate]);

  // Mantine form setup with Zod validation
  const form = usePlaceForm({
    initialValues: getInitialValues(),
    validate: (values) => {
      const result = placeModelSchema.safeParse(values);
      if (!result.success) {
        return result.error.formErrors.fieldErrors;
      }
      return {};
    },
  });

  const isDirty = useMemo(
    () => JSON.stringify(form.values) !== JSON.stringify(getInitialValues()),
    [form.values, getInitialValues],
  );

  // Add new empty subPlace if last one is valid
  const handleAddSubPlace = () => {
    const current = form.values.subPlaces;
    if (
      current.length > 0 &&
      (!current.at(-1)?.title?.trim() ||
        !current.at(-1)?.numberOfWinners ||
        !current.at(-1)?.status)
    ) {
      return;
    }

    const newSubPlaces = [
      ...current,
      { id: uuidv4(), title: "", numberOfWinners: 1, status: "enabled" },
    ];

    form.setFieldValue(
      "subPlaces",
      newSubPlaces.map((sp, i) => ({
        ...sp,
        index: i + 1,
        status: sp.status as "enabled" | "disabled",
      })),
    );
  };

  // Duplicate subPlace by index
  const handleDuplicateSubPlace = (indexToDuplicate: number) => {
    const subPlace = form.values.subPlaces[indexToDuplicate];
    if (
      !subPlace?.title?.trim() ||
      !subPlace?.numberOfWinners ||
      !subPlace?.status
    ) {
      return;
    }

    const duplicated = {
      ...subPlace,
      id: uuidv4(),
      index: form.values.subPlaces.length + 1,
      title: `${subPlace.title}`,
    };

    const newSubPlaces = [...form.values.subPlaces];
    newSubPlaces.splice(indexToDuplicate + 1, 0, duplicated);

    const reIndexed = newSubPlaces.map((sp, i) => ({ ...sp, index: i + 1 }));
    form.setFieldValue("subPlaces", reIndexed);
  };

  // Remove subPlace by index
  const handleRemoveSubPlace = (indexToRemove: number) => {
    const updated = form.values.subPlaces
      .filter((_, i) => i !== indexToRemove)
      .map((sp, i) => ({ ...sp, index: i + 1 }));

    form.setFieldValue("subPlaces", updated);
  };

  // Save form values (locally here)
  const save = async () => {
    const validationResult = placeModelSchema.safeParse(form.values);
    if (validationResult.success) {
      setIsLoading(true);
      try {
        if (onSaveLocally) {
          onSaveLocally(form.values);
        }
        setOpened(false);
      } catch (error) {
        console.error("Error saving form locally", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Convert Zod errors to Mantine form errors
      const fieldErrors = validationResult.error.formErrors.fieldErrors;
      Object.entries(fieldErrors).forEach(([field, messages]) => {
        form.setFieldError(field, messages?.join(", "));
      });
    }
  };

  return (
    <>
      {isLoading && <LoadingOverlay visible overlayOpacity={0.8} />}
      <PlaceFormProvider form={form}>
        <Box
          sx={(theme) => ({
            background:
              theme.colorScheme === "dark"
                ? theme.colors.gray[8]
                : theme.colors.gray[2],
            minHeight: "calc(100vh - 64px)",
          })}
          p={"lg"}
        >
          <Container className="min-h-full">
            <div
              className="sticky top-0 z-50 pb-4"
              style={{ backgroundColor: "#E9ECEF" }}
            >
              <Title order={3} align="center" m="xs">
                {form.values.placeTitle || "New Place"}
              </Title>
              <Breadcrumbs separator="→">
                <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                  Admin
                </Anchor>
                <Anchor component={Link} to={`/${ADMIN_PATH}/pronosticsv2`}>
                  Pronostics
                </Anchor>
                <Anchor onClick={() => setOpened(false)}>Pronostic Form</Anchor>
              </Breadcrumbs>
              <Group position="right">
                <Button
                  disabled={!isDirty}
                  onClick={save}
                  className="bg-button-admin disabled:opacity-75"
                >
                  Ok
                </Button>
              </Group>
            </div>
            <ScrollArea h="calc(100vh - 145px)" type="never">
              <Group className="grid grid-cols-2">
                <TextInput
                  label="Place Title"
                  required
                  {...form.getInputProps("placeTitle")}
                />
              </Group>

              {form.values.subPlaces.map((subPlace, index) => (
                <div key={index}>
                  <Stack spacing="xs" mb="md" py={"xs"}>
                    <Group position="apart" align="center">
                      <Title order={5}>Sub Place #{index + 1}</Title>
                      <Group spacing="xs">
                        <ActionIcon
                          onClick={() => handleDuplicateSubPlace(index)}
                          disabled={
                            !subPlace.title?.trim() ||
                            !subPlace.numberOfWinners ||
                            !subPlace.status
                          }
                        >
                          <Copy size={20} color="blue" />
                        </ActionIcon>
                        <ActionIcon onClick={() => handleRemoveSubPlace(index)}>
                          <Trash color="red" size={20} />
                        </ActionIcon>
                      </Group>
                    </Group>
                    <Card shadow="sm" p="xl" className="overflow-visible">
                      <Group className="grid grid-cols-3 gap-4">
                        <TextInput
                          label="Title"
                          required
                          {...form.getInputProps(`subPlaces.${index}.title`)}
                          className="col-span-2"
                        />
                        <NumberInput
                          label="No of Winners"
                          min={1}
                          {...form.getInputProps(
                            `subPlaces.${index}.numberOfWinners`,
                          )}
                        />
                      </Group>
                      <TextInput
                        label="SubPlace ID"
                        className="mt-4"
                        disabled
                        {...form.getInputProps(`subPlaces.${index}.id`)}
                      />

                      <Radio.Group
                        label="Status"
                        className="mt-4"
                        {...form.getInputProps(`subPlaces.${index}.status`)}
                      >
                        <Group mt="xs">
                          <Radio value="enabled" label="Enabled" />
                          <Radio value="disabled" label="Disabled" />
                        </Group>
                      </Radio.Group>
                    </Card>
                  </Stack>
                </div>
              ))}

              <Group position="center">
                <Button
                  onClick={handleAddSubPlace}
                  variant="subtle"
                  leftIcon={<CirclePlus />}
                  disabled={
                    form.values.subPlaces.length > 0 &&
                    (!form.values.subPlaces.at(-1)?.title?.trim() ||
                      !form.values.subPlaces.at(-1)?.numberOfWinners ||
                      !form.values.subPlaces.at(-1)?.status)
                  }
                >
                  Add SubPlace
                </Button>
              </Group>
            </ScrollArea>
          </Container>
        </Box>
      </PlaceFormProvider>
    </>
  );
}
